import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import DeleteAccountDialog from "@/components/DeleteAccountDialog";
import SubscriptionPanel from "@/components/SubscriptionPanel";
import ReferralShareCard from "@/components/ReferralShareCard";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { SPORTS, FITNESS_LEVELS } from "@/lib/sports";
import { Camera, User as UserIcon, Calendar, Users, Medal, CheckSquareOffset, CaretRight, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", age: "", height_cm: "", weight_kg: "",
    fitness_level: "", sports: [], goals: "", bio: "",
  });
  const [saving, setSaving] = useState(false);
  const [counts, setCounts] = useState({ joined: 0, hosted: 0, completed: 0 });
  const [showDelete, setShowDelete] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    // Load tab counts for the My Activities card.
    (async () => {
      try {
        const [j, h, c] = await Promise.all([
          api.get("/activities/mine?status=joined").catch(() => ({ data: [] })),
          api.get("/activities/mine?status=hosted").catch(() => ({ data: [] })),
          api.get("/activities/mine?status=completed").catch(() => ({ data: [] })),
        ]);
        setCounts({
          joined: (j.data || []).length,
          hosted: (h.data || []).length,
          completed: (c.data || []).length,
        });
      } catch {/* silent */}
    })();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      age: user.age || "",
      height_cm: user.height_cm || "",
      weight_kg: user.weight_kg || "",
      fitness_level: user.fitness_level || "",
      sports: user.sports || [],
      goals: user.goals || "",
      bio: user.bio || "",
    });
  }, [user]);

  if (!user) return null;

  const toggleSport = (key) => {
    setForm((f) => ({
      ...f,
      sports: f.sports.includes(key) ? f.sports.filter((s) => s !== key) : [...f.sports, key],
    }));
  };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      ["age", "height_cm", "weight_kg"].forEach((k) => {
        payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k]);
      });
      const { data } = await api.patch("/users/me", payload);
      setUser(data);
      toast.success(t("profile.saved"));
    } catch (e) {
      toast.error("Couldn't save");
    } finally { setSaving(false); }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser({ ...user, picture: data.url });
      toast.success(t("profile.avatar_updated"));
    } catch (e) { toast.error(t("profile.upload_failed")); }
  };

  const avatarUrl = user.picture
    ? (user.picture.startsWith("http") ? user.picture : `${process.env.REACT_APP_BACKEND_URL}${user.picture}`)
    : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="label-overline text-[#007AFF]">{t("profile.kicker")}</div>
        <h1 className="font-display text-5xl tracking-tight mt-2">{(form.name || "ATHLETE").toUpperCase()}.</h1>

        <div className="grid lg:grid-cols-3 gap-8 mt-10">
          <div>
            <div className="border border-[#262626] bg-[#141414] p-6">
              <div className="relative w-40 h-40 mx-auto">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-40 h-40 object-cover border border-[#262626]" />
                ) : (
                  <div className="w-40 h-40 bg-[#0A0A0A] border border-[#262626] flex items-center justify-center"><UserIcon size={60} weight="duotone" /></div>
                )}
                <button data-testid="avatar-upload-btn" type="button" onClick={() => fileRef.current?.click()} className="kinetic-btn absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[#007AFF] hover:bg-[#0a66d0] text-white px-3 py-2 flex items-center gap-2 text-xs uppercase tracking-widest font-bold">
                  <Camera weight="bold" size={14} /> {t("profile.change")}
                </button>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => uploadAvatar(e.target.files?.[0])} data-testid="avatar-file-input" />
              </div>
              <div className="mt-8 text-center">
                <div className="label-overline">{user.email}</div>
                <div className="text-xs text-zinc-500 mt-1 font-mono-tag">{user.auth_provider}</div>
              </div>
            </div>
          </div>

          <form onSubmit={save} className="lg:col-span-2 space-y-5">
            <Group label={t("common.name")}>
              <input data-testid="profile-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </Group>
            <div className="grid grid-cols-3 gap-3">
              <Group label={t("profile.age")}>
                <input data-testid="profile-age" type="number" min={10} max={120} value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className={inputCls} />
              </Group>
              <Group label={t("profile.height")}>
                <input data-testid="profile-height" type="number" min={80} max={250} value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} className={inputCls} />
              </Group>
              <Group label={t("profile.weight")}>
                <input data-testid="profile-weight" type="number" min={20} max={300} value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className={inputCls} />
              </Group>
            </div>

            <Group label={t("profile.fitness_level")}>
              <div className="flex flex-wrap gap-2">
                {FITNESS_LEVELS.map((l) => (
                  <button type="button" key={l} data-testid={`profile-fitness-${l}`} onClick={() => setForm({ ...form, fitness_level: l })}
                    className={`kinetic-btn px-3 py-2 text-xs uppercase tracking-widest font-bold border ${form.fitness_level === l ? "bg-[#007AFF] border-[#007AFF]" : "border-[#262626] hover:border-white/40 bg-[#141414]"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </Group>

            <Group label={t("profile.sports")}>
              <div className="flex flex-wrap gap-2">
                {SPORTS.map((s) => (
                  <button type="button" key={s.key} data-testid={`profile-sport-${s.key}`} onClick={() => toggleSport(s.key)}
                    className={`kinetic-btn px-3 py-2 text-xs uppercase tracking-widest font-bold border ${form.sports.includes(s.key) ? "bg-[#007AFF] border-[#007AFF]" : "border-[#262626] hover:border-white/40 bg-[#141414]"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </Group>

            <Group label={t("profile.goals")}>
              <input data-testid="profile-goals" value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} placeholder={t("profile.goals_placeholder")} className={inputCls} />
            </Group>

            <Group label={t("profile.bio")}>
              <textarea data-testid="profile-bio" rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className={`${inputCls} resize-none`} />
            </Group>

            <button data-testid="profile-save" disabled={saving} className="kinetic-btn w-full bg-[#007AFF] hover:bg-[#0a66d0] disabled:opacity-50 text-white px-4 py-3 font-bold uppercase tracking-widest text-sm">
              {saving ? t("common.saving") : t("common.save")}
            </button>
          </form>
        </div>

        {/* Subscription management */}
        <SubscriptionPanel />

        {/* Referral section (one-tap share + live counter) */}
        <ReferralShareCard />

        {/* My Activities — links to the dedicated /my-activities page with 3 tabs (matches mobile app). */}
        <Link
          to="/my-activities"
          data-testid="profile-my-activities-card"
          className="mt-6 group border border-[#262626] bg-[#141414] hover:border-white/40 p-6 flex items-center justify-between gap-4 transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-[#007AFF]/15 border border-[#007AFF]">
              <Calendar weight="duotone" size={26} color="#007AFF" />
            </div>
            <div>
              <div className="label-overline">{t("my_activities.card.kicker")}</div>
              <div className="font-display text-2xl tracking-tight mt-1">
                {t("my_activities.title").toUpperCase()}
              </div>
              <div className="mt-2 flex items-center gap-4 text-xs uppercase tracking-widest font-bold">
                <span className="flex items-center gap-1.5"><Users weight="duotone" size={14} color="#FFFFFF" />{counts.joined}</span>
                <span className="flex items-center gap-1.5"><Medal weight="duotone" size={14} color="#FFCC00" />{counts.hosted}</span>
                <span className="flex items-center gap-1.5"><CheckSquareOffset weight="duotone" size={14} color="#34C759" />{counts.completed}</span>
              </div>
            </div>
          </div>
          <CaretRight weight="bold" size={20} className="text-zinc-500 group-hover:text-white transition-colors" />
        </Link>

        {/* Danger zone — account deletion (GDPR right-to-erasure) */}
        <section
          className="mt-10 border border-[#FF3B30]/40 bg-[#FF3B30]/[0.04] p-6"
          data-testid="danger-zone"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="max-w-xl">
              <div className="label-overline" style={{ color: "#FF3B30" }}>
                {lang === "da" ? "Farezone" : "Danger zone"}
              </div>
              <h2 className="font-display text-2xl tracking-tight mt-2 flex items-center gap-2">
                <Warning weight="duotone" size={22} color="#FF3B30" />
                {lang === "da" ? "Slet konto" : "Delete account"}
              </h2>
              <p className="text-sm text-zinc-400 mt-2">
                {lang === "da"
                  ? "Sletter din profil, dine sessioner som vært, dine beskeder og dine vurderinger permanent. Handlingen kan ikke fortrydes."
                  : "Permanently deletes your profile, the sessions you host, your messages and your ratings. This cannot be undone."}
              </p>
            </div>
            <button
              type="button"
              data-testid="delete-account-btn"
              onClick={() => setShowDelete(true)}
              className="kinetic-btn border border-[#FF3B30]/60 hover:bg-[#FF3B30] hover:text-white text-[#FF3B30] px-4 py-3 font-bold uppercase tracking-widest text-xs"
            >
              {lang === "da" ? "Slet min konto" : "Delete my account"}
            </button>
          </div>
        </section>

        {showDelete && (
          <DeleteAccountDialog
            user={user}
            onClose={() => setShowDelete(false)}
            onDeleted={() => {
              setUser(null);
              try { localStorage.removeItem("tf_token"); } catch {/* ignore */}
              toast.success(lang === "da" ? "Konto slettet" : "Account deleted");
              nav("/", { replace: true });
            }}
          />
        )}
      </main>
    </div>
  );
}

const inputCls = "w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#007AFF] px-3 py-3 text-sm outline-none";

function Group({ label, children }) {
  return (
    <div>
      <div className="label-overline mb-2">{label}</div>
      {children}
    </div>
  );
}
